import {
  Component, OnInit, OnDestroy,
  ViewChild, ElementRef, AfterViewChecked, NgZone
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChatService } from '../services/chat.service';

interface Message {
  text: string;
  sender: 'user' | 'bot';
  html?: string;
  isCard?: boolean;
  isTerms?: boolean;   // terms bubble — never spoken aloud
}

// No silence detection — user controls mic manually (tap to start, tap to stop)

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.css']
})
export class ChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  messages: Message[] = [];
  userInput           = '';
  isLoading           = false;
  sessionId           = 'session_' + Date.now();

  voiceMode       = true;   // always on — mic always visible
  isInitializing  = true;   // true during __start__ — hides status bar
  micUsed         = false;  // true only after user taps mic at least once
  voiceActive     = false;  // true only while user is actively in voice conversation
  isListening   = false;
  isSpeaking    = false;
  voiceError    = '';
  voiceStatus   = '';

  private mediaRecorder:   MediaRecorder     | null = null;
  private audioChunks:     Blob[]                   = [];
  private currentAudio:    HTMLAudioElement  | null = null;

  private mimeType:         string                   = '';
  private recordStartTime:  number                   = 0;
  private ttsCancel$        = new Subject<void>();   // cancels in-flight TTS

  constructor(private chatService: ChatService, private ngZone: NgZone) {}

  ngOnInit(): void  { this.startSession(); }
  ngOnDestroy(): void {
    this.stopRecording();
    this.stopAudio();
    this.ttsCancel$.complete();
  }
  ngAfterViewChecked(): void { this.scrollToBottom(); }



  // ── Start recording ───────────────────────────────────────────────────────
  async startListening(): Promise<void> {
    // Stop bot immediately — before any async operations
    if (this.isSpeaking) {
      this.ttsCancel$.next();   // cancel in-flight HTTP request
      this.stopAudio();         // pause any playing audio
    }

    if (this.isListening || this.isLoading) return;

    this.micUsed     = true;
    this.voiceActive = true;  // user explicitly started voice
    this.voiceError  = '';
    this.voiceStatus = '';

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.audioChunks     = [];
      this.recordStartTime = Date.now();

      this.mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']
        .find(t => MediaRecorder.isTypeSupported(t)) || '';

      this.mediaRecorder = new MediaRecorder(
        stream,
        this.mimeType ? { mimeType: this.mimeType } : {}
      );

      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) this.audioChunks.push(e.data);
      };

      this.mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());

        if (Date.now() - this.recordStartTime < 400) {
          this.ngZone.run(() => { this.isListening = false; this.voiceStatus = ''; });
          return;
        }

        this.ngZone.run(() => {
          this.isListening = false;
          this.voiceStatus = 'Converting…';
        });

        // Convert to WAV in browser then send to backend
        try {
          const rawBlob = new Blob(this.audioChunks, { type: this.mimeType || 'audio/webm' });
          const wavBlob = await this.toWav(rawBlob);
          this.ngZone.run(() => { this.voiceStatus = 'Processing…'; this.isLoading = true; });
          this.sendAudioToSTT(wavBlob);
        } catch (err) {
          console.error('[toWav] Error:', err);
          this.ngZone.run(() => {
            this.isListening = false; this.isLoading = false;
            this.voiceStatus = ''; this.voiceError = 'Audio conversion failed. Please try again.';
          });
        }
      };

      this.mediaRecorder.start(100);
      this.ngZone.run(() => {
        this.isListening = true;
        this.voiceStatus = 'Listening… (speak now)';
      });

    } catch (err: any) {
      this.ngZone.run(() => {
        this.voiceError = err.name === 'NotAllowedError'
          ? 'Microphone access denied.'
          : `Microphone error: ${err.message}`;
        if (err.name === 'NotAllowedError') this.voiceMode = false;
      });
    }
  }

  // ── Browser WAV encoder — same logic as fnolbot index.html ───────────────
  private async toWav(audioBlob: Blob): Promise<Blob> {
    console.log('[toWav] blob size=' + audioBlob.size + ' type=' + audioBlob.type);

    const arrayBuffer = await audioBlob.arrayBuffer();
    console.log('[toWav] arrayBuffer byteLength=' + arrayBuffer.byteLength);

    // Decode the recorded audio — close context AFTER decode completes
    const decodeCtx  = new AudioContext();
    let   audioBuffer: AudioBuffer;
    try {
      audioBuffer = await decodeCtx.decodeAudioData(arrayBuffer);
      console.log('[toWav] decoded duration=' + audioBuffer.duration.toFixed(2) + 's sampleRate=' + audioBuffer.sampleRate);
    } catch (decodeErr) {
      console.error('[toWav] decodeAudioData failed:', decodeErr);
      await decodeCtx.close();
      throw decodeErr;
    }
    await decodeCtx.close();

    // Resample to 16kHz mono
    const TARGET_SR  = 16000;
    const numFrames  = Math.ceil(audioBuffer.duration * TARGET_SR);
    if (numFrames < 1) throw new Error('Audio too short to encode');

    const offlineCtx = new OfflineAudioContext(1, numFrames, TARGET_SR);
    const src        = offlineCtx.createBufferSource();
    src.buffer       = audioBuffer;
    src.connect(offlineCtx.destination);
    src.start(0);
    const resampled  = await offlineCtx.startRendering();
    const pcm        = resampled.getChannelData(0);
    console.log('[toWav] resampled frames=' + pcm.length);
    return new Blob([this.encodeWav(pcm, TARGET_SR)], { type: 'audio/wav' });
  }

  private encodeWav(samples: Float32Array, sr: number): ArrayBuffer {
    const byteCount = samples.length * 2;
    const buf       = new ArrayBuffer(44 + byteCount);
    const v         = new DataView(buf);
    const writeStr  = (offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) v.setUint8(offset + i, str.charCodeAt(i));
    };
    writeStr(0, 'RIFF'); v.setUint32(4,  36 + byteCount, true);
    writeStr(8, 'WAVE'); writeStr(12, 'fmt ');
    v.setUint32(16, 16,     true);
    v.setUint16(20, 1,      true);   // PCM
    v.setUint16(22, 1,      true);   // mono
    v.setUint32(24, sr,     true);   // sample rate
    v.setUint32(28, sr * 2, true);   // byte rate
    v.setUint16(32, 2,      true);   // block align
    v.setUint16(34, 16,     true);   // bits per sample
    writeStr(36, 'data'); v.setUint32(40, byteCount, true);
    let off = 44;
    for (let i = 0; i < samples.length; i++) {
      const c = Math.max(-1, Math.min(1, samples[i]));
      v.setInt16(off, c < 0 ? c * 0x8000 : c * 0x7fff, true);
      off += 2;
    }
    return buf;
  }


  stopRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    this.isListening = false;
  }

  // ── Send audio blob to backend → Azure STT ──────────────────────────────
  private sendAudioToSTT(audioBlob: Blob): void {
    this.voiceError = '';
    this.chatService.speechToText(audioBlob).subscribe({
      next: (sttResponse) => {
        const transcript = sttResponse.transcript?.trim();
        this.ngZone.run(() => {
          this.isLoading   = false;
          this.voiceStatus = '';
          if (!transcript) {
            console.warn('[STT] No transcript returned');
            this.voiceActive = false;
            this.voiceError  = 'לא זוהתה דיבור. לחץ על המיקרופון ונסה שוב.';
            return;
          }
          console.log('[STT] Transcript:', transcript);
          this.userInput   = transcript;
          this.voiceActive = true;  // came from mic — bot should speak reply
          this.onSend();
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          this.isLoading   = false;
          this.voiceStatus = '';
          this.voiceError  = 'Speech recognition error. Please try again.';
          console.error('[STT] Error:', err);
          // Do NOT auto-retry on error — user must tap mic manually
        });
      }
    });
  }

  // ── Azure TTS ─────────────────────────────────────────────────────────────
  private speakReply(text: string): void {
    if (!this.voiceMode || !this.micUsed || !text.trim()) return;
    // ── TTS text cleaning ────────────────────────────────────────────────────
    let cleanText = text
      .replace(/<[^>]+>/g, ' ')                              // strip HTML tags
      .replace(/\[([^\]]+)\]\(https?:\/\/[^\s)]+\)/g, '$1') // [text](url) -> text
      .replace(/https?:\/\/\S+/g, '')                    // strip raw URLs
      .replace(/[*_`#~]/g, '');                              // strip markdown

    // Issue 2: remove emojis — Azure TTS reads them aloud
    cleanText = cleanText.replace(/[\u{1F300}-\u{1FFFF}\u{2600}-\u{27BF}]/gu, '');

    // Issue 3: expand license plate digits to be read individually (e.g. 8822212 → 8 8 2 2 2 1 2)
    cleanText = cleanText.replace(/\b(\d{5,8})\b/g, (m) => m.split('').join(' '));

    // Issue 5: replace "/" with " או " (Hebrew "or") for yes/no prompts, avoid math reading
    cleanText = cleanText.replace(/(\S+)\/(\S+)/g, '$1 או $2');

    // Issue 4: fix מעולה pronunciation — Azure sometimes mispronounces it;
    // use phoneme hint via SSML (handled in backend), here just ensure it's clean
    // (no client-side fix needed — backend SSML handles it)

    // Issue 8: replace MBI with חובה in speech
    cleanText = cleanText.replace(/\bMBI\b/g, 'חובה');

    cleanText = cleanText.replace(/\s{2,}/g, ' ').trim();
    if (!cleanText) return;

    this.ttsCancel$.next();   // cancel any previous TTS
    this.stopAudio();
    this.isSpeaking = true;

    this.chatService.textToSpeech(cleanText)
      .pipe(takeUntil(this.ttsCancel$))
      .subscribe({
      next: (audioBlob: Blob) => {
        // Discard if user interrupted before response arrived
        if (!this.isSpeaking) {
          console.log('[TTS] Discarded — interrupted');
          return;
        }
        // Re-wrap blob with explicit audio/mpeg type — fixes ERR_REQUEST_RANGE_NOT_SATISFIABLE
        const mp3Blob  = new Blob([audioBlob], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(mp3Blob);
        console.log('[TTS] blob size=' + mp3Blob.size + ' type=' + mp3Blob.type);
        this.currentAudio = new Audio(audioUrl);
        this.currentAudio.onended = () => {
          this.isSpeaking  = false;
          this.voiceActive = false;
          URL.revokeObjectURL(audioUrl);
          // No auto-restart — user must tap mic manually each time
        };
        this.currentAudio.onerror = () => {
          this.isSpeaking = false;
          this.voiceActive = false;
          URL.revokeObjectURL(audioUrl);
        };
        // Unlock autoplay (Chrome requires user-gesture-linked audio context)
        this.currentAudio.play().catch((playErr) => {
          console.warn('[TTS] play() blocked by autoplay policy:', playErr);
          this.isSpeaking  = false;
          this.voiceActive = false;
          URL.revokeObjectURL(audioUrl);
        });
      },
      error: (err: any) => {
        console.error('[TTS] API error:', err);
        this.isSpeaking  = false;
        this.voiceActive = false;
      }
    });
  }

  private stopAudio(): void {
    if (this.currentAudio) {
      this.currentAudio.onended = null;
      this.currentAudio.onerror = null;
      this.currentAudio.onended = null;
      this.currentAudio.onerror = null;
      try { this.currentAudio.pause(); } catch(e) {}
      this.currentAudio.src = '';
      this.currentAudio     = null;
    }
    this.isSpeaking = false;
  }

  private startSession(): void {
    this.chatService.clearSession(this.sessionId).subscribe({
      complete: () => this.sendMessage('__start__', true),
      error:    () => this.sendMessage('__start__', true)
    });
  }

  onSend(): void {
    const text = this.userInput.trim();
    if (!text || this.isLoading) return;
    this.userInput = '';
    // Stop bot speaking if user sends a message
    if (this.isSpeaking) { this.ttsCancel$.next(); this.stopAudio(); }
    const speakReply = this.voiceActive;
    this.voiceActive = false;  // reset after use
    this.sendMessage(text, false, speakReply);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') this.onSend();
  }

  private sendMessage(text: string, isHidden: boolean, speakBack = false): void {
    if (!isHidden) this.messages.push({ text, sender: 'user' });
    this.isLoading = true;
    this.chatService.sendMessage(text, this.sessionId).subscribe({
      next: (response) => {
        this.isLoading      = false;
        this.isInitializing = false;
        const replyTrimmed = (response.reply || '').trim();
        const isHtmlCard = (response as any).reply_type === 'html'
          || replyTrimmed.indexOf('wsq-card') !== -1;   // content-based detection — works even if reply_type is missing
        console.log('[Chat] isHtmlCard=' + isHtmlCard + ' reply_type=' + (response as any).reply_type + ' preview=' + replyTrimmed.substring(0, 40));
        this.messages.push({
          text: response.reply, sender: 'bot',
          html: isHtmlCard ? response.reply : this.formatMessage(response.reply),
          isCard: isHtmlCard
        });
        // Terms & privacy bubble — shown only on session start
        if ((response as any).terms_msg) {
          this.messages.push({
            text: (response as any).terms_msg,
            sender: 'bot',
            html: this.formatMessage((response as any).terms_msg),
            isTerms: true
          });
        }
        // Only speak if message came from mic — not from text box
        if (speakBack) this.speakReply(response.reply);
      },
      error: () => {
        this.isLoading      = false;
        this.isInitializing = false;
        const errMsg = 'מצטערים, אירעה שגיאה. אנא נסה שנית.';
        this.messages.push({ text: errMsg, sender: 'bot', html: errMsg });
        if (speakBack) this.speakReply(errMsg);
      }
    });
  }

  private formatMessage(text: string): string {
    const escaped = text.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

    // Step 1: convert markdown links [text](url) -> placeholder tokens (avoids double-matching)
    const placeholders: string[] = [];
    let withPlaceholders = escaped.replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      (_match, linkText, url) => {
        const html = '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + linkText + '</a>';
        placeholders.push(html);
        return '\u0000' + (placeholders.length - 1) + '\u0000';
      }
    );

    // Step 2: fallback — any remaining RAW urls (not already converted) become clickable
    withPlaceholders = withPlaceholders.replace(
      /(https?:\/\/[^\s<]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
    );

    // Step 3: restore placeholders back to their final HTML
    withPlaceholders = withPlaceholders.replace(/\u0000(\d+)\u0000/g,
      (_match, idx) => placeholders[parseInt(idx, 10)]
    );

    return withPlaceholders.replace(/\n/g, '<br>');
  }

  private scrollToBottom(): void {
    try {
      this.messagesContainer.nativeElement.scrollTop =
        this.messagesContainer.nativeElement.scrollHeight;
    } catch (e) {}
  }
}